import PropTypes from 'prop-types';
import {
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Stack,
    Typography,
    Tooltip,
    IconButton,
    CircularProgress,
    Box,
    Button
} from '@mui/material';
import { ReceiptLongOutlined, ContentCopyOutlined, CalendarMonthOutlined, EmailOutlined, PhoneAndroidOutlined } from '@mui/icons-material';
import moment from 'moment';

import { globalNotifySuccess } from 'views/utilities/AlertToast';
import { formatINR } from '../credit-limits/creditLimitsUtils';

const TransactionHistoryTable = ({
    loading = false,
    transactions = [],
    totalCount = 0,
    page = 0,
    rowsPerPage = 20,
    onPageChange,
    onRowsPerPageChange,
    usageFromDate,
    repaymentToDate,
    onResetFilter
}) => {
    return (
        <>
            <TableContainer sx={{ minHeight: 400 }}>
                <Table sx={{ minWidth: 950 }}>
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', py: 1.8 }}>EMPLOYEE</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' }}>CONTACT INFO</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                USAGE (CYCLE)
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                REPAYMENT SETTLED
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' }}>SETTLEMENT DETAILS</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                POST-REPAYMENT AMOUNT
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} sx={{ py: 10, textAlign: 'center' }}>
                                    <CircularProgress size={36} sx={{ color: '#1e40af' }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                        Loading repayment history...
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : transactions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} sx={{ py: 10, textAlign: 'center' }}>
                                    <Box
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: '50%',
                                            bgcolor: '#f1f5f9',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto',
                                            color: '#94a3b8',
                                            mb: 2
                                        }}
                                    >
                                        <ReceiptLongOutlined sx={{ fontSize: 32 }} />
                                    </Box>
                                    <Typography variant="h6" fontWeight={700} color="text.primary">
                                        No Repayment Records Found
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 450, mx: 'auto', mt: 0.5 }}>
                                        No employee repayment records found for the cycle from <strong>{usageFromDate}</strong> to{' '}
                                        <strong>{repaymentToDate}</strong>. Try modifying the cycle month, date filters, or clearing search.
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={onResetFilter}
                                        sx={{ mt: 2, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                                    >
                                        Reset Filters
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ) : (
                            transactions.map((item, index) => {
                                const empId = item.crossbanking_id ?? item.id ?? 'N/A';
                                const empName = item.crossbanking_name || item.name || 'Unnamed Employee';
                                const mobile = item.mobileno || item.mobile || '';
                                const email = item.email || '';

                                const rechargeAmt = Number(item.recharge || 0);
                                const billPaymentAmt = Number(item.billpayment || 0);
                                const totalUsedAmt = Number(item.totalused ?? item.amount ?? 0);

                                const repaymentAmt = Number(item.credit_repayment ?? item.paid_amount ?? 0);
                                const latestRepaymentAmt =
                                    item.latest_credit_repayment !== null && item.latest_credit_repayment !== undefined
                                        ? Number(item.latest_credit_repayment)
                                        : null;

                                const dateVal = item.credit_repayment_date || item.settled_at || item.created_at;
                                const formattedDate =
                                    dateVal && moment(dateVal).isValid()
                                        ? moment(dateVal).format('DD MMM YYYY, hh:mm A')
                                        : dateVal || 'N/A';

                                const rrn = item.rrn || 'N/A';
                                const postRepaymentAmt = Number(item.total_used_after_repayment ?? item.billpayment_after_repayment ?? 0);

                                return (
                                    <TableRow
                                        key={empId !== 'N/A' ? empId : index}
                                        hover
                                        sx={{
                                            '&:last-child td, &:last-child th': { border: 0 }
                                        }}
                                    >
                                        {/* Employee Name */}
                                        <TableCell sx={{ py: 1.8 }}>
                                            <Typography variant="body2" fontWeight={700} color="text.primary">
                                                {empName}
                                            </Typography>
                                        </TableCell>

                                        {/* Contact Info (Mobile & Email) */}
                                        <TableCell>
                                            <Stack spacing={0.5}>
                                                {mobile ? (
                                                    <Stack direction="row" spacing={0.8} alignItems="center">
                                                        <PhoneAndroidOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                        <Typography variant="caption" color="text.primary" fontWeight={600}>
                                                            {mobile}
                                                        </Typography>
                                                    </Stack>
                                                ) : null}
                                                {email ? (
                                                    <Stack direction="row" spacing={0.8} alignItems="center">
                                                        <EmailOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {email}
                                                        </Typography>
                                                    </Stack>
                                                ) : null}
                                                {!mobile && !email && (
                                                    <Typography variant="caption" color="text.disabled">
                                                        N/A
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </TableCell>

                                        {/* Usage Breakdown */}
                                        <TableCell sx={{ textAlign: 'right' }}>
                                            <Typography variant="body2" fontWeight={800} color="#0f172a" fontSize="0.95rem">
                                                {formatINR(totalUsedAmt)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                                                Recharge: {formatINR(rechargeAmt)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                Bills: {formatINR(billPaymentAmt)}
                                            </Typography>
                                        </TableCell>

                                        {/* Repayment Settled */}
                                        <TableCell sx={{ textAlign: 'right' }}>
                                            <Typography variant="body2" fontWeight={800} color="#15803d" fontSize="0.95rem">
                                                {formatINR(repaymentAmt)}
                                            </Typography>
                                            {latestRepaymentAmt !== null && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                                                    Latest: {formatINR(latestRepaymentAmt)}
                                                </Typography>
                                            )}
                                        </TableCell>

                                        {/* Settlement Details (Date & RRN) */}
                                        <TableCell>
                                            <Stack direction="row" spacing={0.8} alignItems="center">
                                                <CalendarMonthOutlined sx={{ fontSize: 15, color: 'text.secondary' }} />
                                                <Typography variant="body2" color="text.primary" fontSize="0.82rem">
                                                    {formattedDate}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                    RRN: {rrn}
                                                </Typography>
                                                {rrn !== 'N/A' && (
                                                    <Tooltip title="Copy RRN">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigator.clipboard.writeText(String(rrn));
                                                                globalNotifySuccess('Bank RRN copied');
                                                            }}
                                                            sx={{ p: 0.2 }}
                                                        >
                                                            <ContentCopyOutlined sx={{ fontSize: 13, color: 'text.disabled' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>

                                        {/* Post-Repayment Amount */}
                                        <TableCell sx={{ textAlign: 'right' }}>
                                            <Typography variant="body2" fontWeight={800} color="#b45309" fontSize="0.95rem">
                                                {formatINR(postRepaymentAmt)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination Controls */}
            <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                component="div"
                count={totalCount || transactions.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => onPageChange(newPage)}
                onRowsPerPageChange={(e) => {
                    const newPerPage = parseInt(e.target.value, 10);
                    onRowsPerPageChange(newPerPage);
                }}
                sx={{
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    bgcolor: '#fff'
                }}
            />
        </>
    );
};

TransactionHistoryTable.propTypes = {
    loading: PropTypes.bool,
    transactions: PropTypes.arrayOf(PropTypes.object),
    totalCount: PropTypes.number,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onRowsPerPageChange: PropTypes.func.isRequired,
    usageFromDate: PropTypes.string.isRequired,
    repaymentToDate: PropTypes.string.isRequired,
    onResetFilter: PropTypes.func.isRequired
};

export default TransactionHistoryTable;
